<script>
import * as m from '@app/paraglide/messages.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import tcLib from '@cclib/lib/timecode.js';

/** @type {{startSec?: number, endSec?: number, durationSec?: number, disabled?: boolean}} */
let {
    startSec = $bindable(0),
    endSec = $bindable(0),
    durationSec = 0,
    disabled = false
} = $props();

/* Frames are the canonical unit for the slider; integer frames keep the SMPTE
   labels exact and avoid floating point drift as the handles are dragged. */
let totalFrames = $derived(Math.max(1, tcLib.secToFrames(durationSec, projectState.frameRate)));
let startFrame = $derived(clampFrame(tcLib.secToFrames(startSec, projectState.frameRate)));
let endFrame = $derived(clampFrame(tcLib.secToFrames(endSec, projectState.frameRate)));

/* Displayed timecode. Offset by the project incode so labels match the rest of
   the app, even though the encode itself works in media relative seconds. */
let startTc = $derived(formatTc(startSec + projectState.incode));
let endTc = $derived(formatTc(endSec + projectState.incode));
let durationTc = $derived(formatTc(Math.max(0, endSec - startSec)));

/* Selected span highlight */
let selectionLeft = $derived((startFrame / totalFrames) * 100);
let selectionWidth = $derived(((endFrame - startFrame) / totalFrames) * 100);

/* Both thumbs sit in the same space. When the in point is dragged close to the
   end of the media the two overlap, so lift the start handle above the end one
   to keep it grabbable. */
let startHandleOnTop = $derived(startFrame > totalFrames * 0.9);

function clampFrame(frame) {
    if (!Number.isFinite(frame)) {
        return 0;
    }

    return Math.min(Math.max(Math.round(frame), 0), totalFrames);
}

function formatTc(sec) {
    try {
        return tcLib.secToTc(Math.max(0, sec), projectState.frameRate, projectState.dropFrame);
    } catch (err) {
        return tcLib.secToTc(0, projectState.frameRate, projectState.dropFrame);
    }
}

function frameToSec(frame) {
    return tcLib.framesToSec(frame, projectState.frameRate);
}

function updateStartFrame(frame) {
    /* Start always stays at least one frame behind the out point */
    const nextFrame = Math.max(0, Math.min(clampFrame(frame), endFrame - 1));
    startSec = frameToSec(nextFrame);

    return nextFrame;
}

function updateEndFrame(frame) {
    /* End always stays at least one frame ahead of the in point */
    const nextFrame = Math.min(totalFrames, Math.max(clampFrame(frame), startFrame + 1));
    endSec = frameToSec(nextFrame);

    return nextFrame;
}

function handleStartSlider(e) {
    if (disabled) {
        return;
    }

    /* Write the clamped frame straight back to the element: when the drag is
       clamped the derived value does not change, so Svelte would not correct
       the thumb position on its own. */
    e.target.value = updateStartFrame(parseInt(e.target.value, 10));
}

function handleEndSlider(e) {
    if (disabled) {
        return;
    }

    e.target.value = updateEndFrame(parseInt(e.target.value, 10));
}

/* The shapes tcValidate can actually parse. Anything else makes it fall back to
   00:00:00:00, which would silently move a handle to the head of the media, so
   unparsable entries are rejected instead. */
function isParsableTc(tc) {
    const raw = tc.trim().toUpperCase();

    return /^\d\d:\d\d:\d\d[:;]\d\d$/.test(raw) ||
        /^\d{8}$/.test(raw) ||
        /^\d+[HMSF]$/.test(raw) ||
        /^\d+$/.test(raw);
}

function tcToFrame(tc) {
    if (!isParsableTc(tc)) {
        return null;
    }

    const validatedTc = tcLib.tcValidate(tc, projectState.frameRate, projectState.dropFrame);
    if (!validatedTc) {
        return null;
    }

    const sec = tcLib.tcToSec(validatedTc, projectState.frameRate, projectState.dropFrame) - projectState.incode;

    return clampFrame(tcLib.secToFrames(sec, projectState.frameRate));
}

function validateStartTc(e) {
    if (disabled) {
        return;
    }

    try {
        const frame = tcToFrame(e.target.value);
        if (frame !== null) {
            updateStartFrame(frame);
        }
    } catch (err) {
        console.log(err.message);
    }

    /* Re-render from state so an invalid or clamped entry snaps back */
    e.target.value = startTc;
    e.target.blur();
}

function validateEndTc(e) {
    if (disabled) {
        return;
    }

    try {
        const frame = tcToFrame(e.target.value);
        if (frame !== null) {
            updateEndFrame(frame);
        }
    } catch (err) {
        console.log(err.message);
    }

    e.target.value = endTc;
    e.target.blur();
}

function frameByFrame(e, isStart) {
    if (disabled || (e.key !== "ArrowUp" && e.key !== "ArrowDown")) {
        return;
    }

    e.preventDefault();
    const step = e.key === "ArrowUp" ? 1 : -1;

    if (isStart) {
        updateStartFrame(startFrame + step);
        e.target.value = startTc;
    } else {
        updateEndFrame(endFrame + step);
        e.target.value = endTc;
    }
}
</script>

<div class="tc-range">
    <div class="tc-range-slider">
        <div class="tc-range-track" aria-hidden="true"></div>
        <div class="tc-range-selection"
             aria-hidden="true"
             style="left: {selectionLeft}%; width: {selectionWidth}%;"></div>
        <input type="range"
               id="tcRangeStart"
               class="form-range tc-range-input {startHandleOnTop ? 'tc-range-input-top' : ''}"
               aria-label={m.video_export_segment_start_aria()}
               min="0"
               max={totalFrames}
               step="1"
               value={startFrame}
               oninput={handleStartSlider}
               {disabled}>
        <input type="range"
               id="tcRangeEnd"
               class="form-range tc-range-input"
               aria-label={m.video_export_segment_end_aria()}
               min="0"
               max={totalFrames}
               step="1"
               value={endFrame}
               oninput={handleEndSlider}
               {disabled}>
    </div>

    <div class="row g-2 mt-2">
        <div class="col-4">
            <label class="form-label small mb-1" for="tcRangeStartInput">{m.video_export_segment_start_label()}</label>
            <input type="text"
                   id="tcRangeStartInput"
                   class="form-control form-control-sm border"
                   placeholder={m.input_tc_generic_placeholder()}
                   value={startTc}
                   title={startTc}
                   onkeydown={(e) => frameByFrame(e, true)}
                   onblur={validateStartTc}
                   onkeyup={(e) => e.key === 'Enter' && validateStartTc(e)}
                   onfocus={(e) => e.target.select()}
                   {disabled}>
        </div>
        <div class="col-4">
            <label class="form-label small mb-1" for="tcRangeEndInput">{m.video_export_segment_end_label()}</label>
            <input type="text"
                   id="tcRangeEndInput"
                   class="form-control form-control-sm border"
                   placeholder={m.input_tc_generic_placeholder()}
                   value={endTc}
                   title={endTc}
                   onkeydown={(e) => frameByFrame(e, false)}
                   onblur={validateEndTc}
                   onkeyup={(e) => e.key === 'Enter' && validateEndTc(e)}
                   onfocus={(e) => e.target.select()}
                   {disabled}>
        </div>
        <div class="col-4">
            <span class="form-label small mb-1 d-block">{m.video_export_segment_duration_label()}</span>
            <p class="form-control-plaintext form-control-sm text-dark mb-0" role="status" aria-live="polite">{durationTc}</p>
        </div>
    </div>
</div>

<style lang="css">
.tc-range-slider {
    /* Matches Bootstrap's .form-range height so the native thumbs stay centred
       over the track we draw ourselves */
    position: relative;
    height: 1.5rem;
}

.tc-range-track {
    position: absolute;
    top: 50%;
    left: 0;
    right: 0;
    height: 0.5rem;
    margin-top: -0.25rem;
    border-radius: 1rem;
    background-color: #dee2e6;
}

.tc-range-selection {
    position: absolute;
    top: 50%;
    height: 0.5rem;
    margin-top: -0.25rem;
    border-radius: 1rem;
    background-color: #0dcaf0;
}

.tc-range-input {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 1.5rem;
    margin: 0;
    background: none;
    /* Let clicks fall through the track so only the thumbs are grabbable */
    pointer-events: none;
}

.tc-range-input-top {
    z-index: 2;
}

.tc-range-input::-webkit-slider-runnable-track {
    background: none;
    border: none;
}

.tc-range-input::-moz-range-track {
    background: none;
    border: none;
}

.tc-range-input::-webkit-slider-thumb {
    pointer-events: auto;
}

.tc-range-input::-moz-range-thumb {
    pointer-events: auto;
}

.tc-range-input:disabled::-webkit-slider-thumb {
    pointer-events: none;
}

.tc-range-input:disabled::-moz-range-thumb {
    pointer-events: none;
}

.small {
    font-size: 11px;
}
</style>
